import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button } from 'react-bootstrap';
import traceabilityService from '../services/traceabilityService';
import { t } from '../context/LanguageContext';

// ── Per-row lot code input with autocomplete + integrated inventory status ─────

function LotCodeInput({ row, onUpdate, onRemove }) {
  const token = localStorage.getItem('token');
  const [suggestions, setSuggestions]         = useState([]);
  const [showDropdown, setShowDropdown]       = useState(false);
  const [inventoryStatus, setInventoryStatus] = useState(null); // { exists, quantity }
  const [invLoading, setInvLoading]           = useState(false);
  const debounceRef = useRef(null);

  const fetchInventory = async (lotCode, productId) => {
    if (!lotCode.trim() || !productId) return;
    setInvLoading(true);
    try {
      const resp = await traceabilityService.getLotInventory(productId, lotCode.trim(), token);
      setInventoryStatus(resp.data);
    } catch {
      setInventoryStatus({ exists: false, quantity: 0 });
    } finally {
      setInvLoading(false);
    }
  };

  const handleChange = (value) => {
    onUpdate('lotCode', value);
    setInventoryStatus(null);
    clearTimeout(debounceRef.current);
    if (value.length >= 1) {
      debounceRef.current = setTimeout(async () => {
        try {
          const resp = await traceabilityService.searchBatchLotCodes(value, row.productId, token);
          const raw = resp.data || [];
          const codes = raw.map(item => (typeof item === 'string' ? item : item.lotCode));
          // Only show lot codes that have positive inventory for this product
          const invResults = await Promise.all(
            codes.map(code =>
              traceabilityService.getLotInventory(row.productId, code, token)
                .then(r => ({ code, quantity: r.data?.quantity ?? 0, exists: r.data?.exists ?? false }))
                .catch(() => ({ code, quantity: 0, exists: false }))
            )
          );
          const filtered = invResults.filter(r => r.exists && r.quantity > 0).map(r => r.code);
          setSuggestions(filtered);
          setShowDropdown(filtered.length > 0);
        } catch {
          setSuggestions([]);
          setShowDropdown(false);
        }
      }, 220);
    } else {
      setSuggestions([]);
      setShowDropdown(false);
    }
  };

  const handleSelect = (code) => {
    onUpdate('lotCode', code);
    setShowDropdown(false);
    setSuggestions([]);
    fetchInventory(code, row.productId);
  };

  const handleBlur = () => {
    setTimeout(() => {
      setShowDropdown(false);
      if (row.lotCode.trim()) fetchInventory(row.lotCode, row.productId);
    }, 180);
  };

  // Determine border color and appended text from inventory status
  const enteredQty = Number(row.quantity) || 0;
  const overStock  = inventoryStatus?.exists && enteredQty > 0 && enteredQty > inventoryStatus.quantity;

  let borderColor;
  let appendText = null;
  let appendStyle = {};

  if (invLoading) {
    appendText = <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12 }} />;
    appendStyle = { backgroundColor: '#fff' };
  } else if (inventoryStatus !== null) {
    if (!inventoryStatus.exists) {
      borderColor = '#dc3545';
      appendText = 'Not found';
      appendStyle = { fontSize: '0.72rem', backgroundColor: '#fff3f3', borderColor: '#dc3545', color: '#dc3545' };
    } else if (inventoryStatus.quantity <= 0) {
      borderColor = '#dc3545';
      appendText = '0 in stock';
      appendStyle = { fontSize: '0.72rem', backgroundColor: '#fff3f3', borderColor: '#dc3545', color: '#dc3545' };
    } else if (overStock) {
      borderColor = '#dc3545';
      appendText = `only ${inventoryStatus.quantity} in stock`;
      appendStyle = { fontSize: '0.72rem', backgroundColor: '#fff3f3', borderColor: '#dc3545', color: '#dc3545' };
    } else {
      borderColor = '#198754';
      appendText = `${inventoryStatus.quantity} in stock`;
      appendStyle = { fontSize: '0.72rem', color: '#198754' };
    }
  }

  return (
    <tr>
      <td style={{ position: 'relative' }}>
        <div className="input-group input-group-sm">
          <input
            className="form-control form-control-sm"
            placeholder="e.g. C-126221"
            value={row.lotCode}
            onChange={e => handleChange(e.target.value)}
            onBlur={handleBlur}
            autoComplete="off"
            style={{ borderColor: borderColor || undefined }}
          />
          {(appendText !== null) && (
            <span className="input-group-text" style={appendStyle}>
              {appendText}
            </span>
          )}
        </div>
        {showDropdown && (
          <ul
            className="list-group position-absolute shadow-sm"
            style={{ zIndex: 1060, top: '100%', left: 0, right: 0, maxHeight: 160, overflowY: 'auto' }}
          >
            {suggestions.map((code, i) => (
              <li
                key={i}
                className="list-group-item list-group-item-action py-1"
                style={{ fontSize: 12, cursor: 'pointer' }}
                onMouseDown={() => handleSelect(code)}
              >
                {code}
              </li>
            ))}
          </ul>
        )}
      </td>
      <td>
        <input
          className="form-control form-control-sm"
          type="number"
          min="0"
          placeholder="units"
          value={row.quantity}
          onChange={e => onUpdate('quantity', e.target.value)}
          style={{ borderColor: overStock ? '#dc3545' : undefined }}
        />
      </td>
      <td>
        <button className="btn btn-outline-danger btn-sm px-2" onClick={onRemove}>✕</button>
      </td>
    </tr>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function totalUnitsForItem(item) {
  const qty = Number(item.quantity || item.totalQty || 0);
  const upc = Number(item.unitsPerCase || 1);
  const unit = item.quantityUnit;
  return unit === 'cases' && upc > 1 ? qty * upc : qty;
}

function totalUnitsLabel(item) {
  const qty = Number(item.quantity || item.totalQty || 0);
  const upc = Number(item.unitsPerCase || 1);
  const unit = item.quantityUnit;
  if (unit === 'cases' && upc > 1) {
    return `${qty} cs × ${upc} u = ${qty * upc} units`;
  }
  return `${qty} units`;
}

// ── LotCodeModal ──────────────────────────────────────────────────────────────

export default function LotCodeModal({ order, show, onClose, onSaved, lang }) {
  const [rows, setRows]     = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  useEffect(() => {
    if (!show || !order) return;
    setError('');
    setRows(
      (order.Items || []).flatMap(item => ([{
        orderItemId:  item.id,
        productId:    item.productId,
        productName:  item.Product?.name || item.presentationLabel || '—',
        totalQty:     item.quantity,
        quantityUnit: item.quantityUnit,
        unitsPerCase: item.unitsPerCase,
        lotCode:      '',
        quantity:     '',
        _key:         `${item.id}-0`,
      }]))
    );
  }, [show, order]);

  const addRow = (group) => {
    setRows(prev => [...prev, {
      orderItemId:  group.orderItemId,
      productId:    group.productId,
      productName:  group.productName,
      totalQty:     group.totalQty,
      quantityUnit: group.quantityUnit,
      unitsPerCase: group.unitsPerCase,
      lotCode:      '',
      quantity:     '',
      _key:         `${group.orderItemId}-${Date.now()}`,
    }]);
  };

  const updateRow = (key, field, value) =>
    setRows(prev => prev.map(r => r._key === key ? { ...r, [field]: value } : r));

  const removeRow = (key) =>
    setRows(prev => prev.filter(r => r._key !== key));

  const handleSave = async () => {
    const filled = rows.filter(r => r.lotCode.trim() && r.quantity);
    if (!filled.length) { onClose(); return; }
    if (anyOverLimit) return; // blocked — UI already shows the error
    setSaving(true);
    setError('');
    try {
      const { default: customerOrderService } = await import('../services/customerOrderService');
      await customerOrderService.saveLotCodes(order.id, filled.map(r => ({
        orderItemId: r.orderItemId,
        productId:   r.productId,
        lotCode:     r.lotCode.trim(),
        quantity:    Number(r.quantity),
      })));
      onSaved();
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const itemGroups = (order?.Items || []).map(item => {
    const groupRows = rows.filter(r => r.orderItemId === item.id);
    const enteredUnits = groupRows.reduce((s, r) => s + (Number(r.quantity) || 0), 0);
    const maxUnits = totalUnitsForItem(item);
    return {
      orderItemId:  item.id,
      productId:    item.productId,
      productName:  item.Product?.name || item.presentationLabel || '—',
      totalQty:     item.quantity,
      quantityUnit: item.quantityUnit,
      unitsPerCase: item.unitsPerCase,
      rows:         groupRows,
      enteredUnits,
      maxUnits,
      overLimit:    enteredUnits > maxUnits,
    };
  });

  const anyOverLimit = itemGroups.some(g => g.overLimit);

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>
          Lot Codes — {order?.orderNumber}
          <span className="text-muted fw-normal ms-2" style={{ fontSize: 13 }}>
            (optional — skip if using BOL)
          </span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <div className="alert alert-danger py-2 px-3 mb-3" style={{ fontSize: 13 }}>{error}</div>
        )}
        <div className="alert alert-info py-2 px-3 mb-3" style={{ fontSize: 12 }}>
          Enter lot code(s) per product. Quantities should be in <strong>units</strong>.
          Multiple lot codes are allowed per item. Leave blank to skip.
        </div>
        {itemGroups.map(group => (
          <div key={group.orderItemId} className="mb-3">
            <div className="d-flex align-items-center justify-content-between mb-1">
              <strong style={{ fontSize: 13 }}>{group.productName}</strong>
              <span style={{ fontSize: 12 }}>
                {group.overLimit ? (
                  <span className="text-danger fw-semibold">
                    {group.enteredUnits} / {group.maxUnits} units — over by {group.enteredUnits - group.maxUnits}
                  </span>
                ) : group.enteredUnits === group.maxUnits && group.maxUnits > 0 ? (
                  <span className="text-success fw-semibold">
                    ✓ {group.maxUnits} / {group.maxUnits} units
                  </span>
                ) : group.enteredUnits > 0 ? (
                  <span className="text-warning fw-semibold">
                    {group.enteredUnits} / {group.maxUnits} units — {group.maxUnits - group.enteredUnits} remaining
                  </span>
                ) : (
                  <span className="text-muted">{totalUnitsLabel(group)}</span>
                )}
              </span>
            </div>
            <table className="table table-sm mb-1" style={{ fontSize: 13 }}>
              <thead className="table-light">
                <tr>
                  <th>Lot Code</th>
                  <th style={{ width: 130 }}>Qty (units)</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {group.rows.map(row => (
                  <LotCodeInput
                    key={row._key}
                    row={row}
                    onUpdate={(field, value) => updateRow(row._key, field, value)}
                    onRemove={() => removeRow(row._key)}
                  />
                ))}
              </tbody>
            </table>
            <button
              className="btn btn-outline-secondary btn-sm"
              style={{ fontSize: 12 }}
              onClick={() => addRow(group)}
            >
              + Add lot code
            </button>
          </div>
        ))}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onClose}>
          {t('Skip', 'Omitir', lang)}
        </Button>
        <Button variant="primary" size="sm" onClick={handleSave} disabled={saving || anyOverLimit}>
          {saving
            ? <><span className="spinner-border spinner-border-sm me-1" />Saving…</>
            : t('Save Lot Codes', 'Guardar Lotes', lang)}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
